"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "@/lib/store";
import { entityRouteUrl, readEntityRoute, type EntityRoute, type EntityTarget } from "@/lib/entityRoute";

type Resolution = "loading" | "ready" | "unavailable" | "error";

/** One owner for URL ↔ entity selection. Hydration and breakpoints never write intent. */
export function useEntityRoute(initialSlug: string | undefined, onView: (view: string) => void) {
  const hydrated = useStore((s) => s.hydrated);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const currentUserId = useStore((s) => s.currentUserId);
  const workspaces = useStore((s) => s.workspaces);
  const docs = useStore((s) => s.docs);
  const issues = useStore((s) => s.issues);
  const notes = useStore((s) => s.notes);
  const error = useStore((s) => s.hydrationError);
  const [route, setRoute] = useState<EntityRoute | null>(null);
  const [resolution, setResolution] = useState<{ key: string; status: Resolution } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const applying = useRef(false);
  const intent = useRef<EntityRoute | null>(null);
  const viewCallback = useRef(onView);
  viewCallback.current = onView;

  const applySelection = useCallback((target: EntityTarget | null) => {
    applying.current = true;
    try {
      const state = useStore.getState();
      state.selectDoc(target?.kind === "doc" ? target.id : null);
      state.selectIssue(target?.kind === "issue" ? target.id : null);
      state.selectNote(target?.kind === "note" ? target.id : null);
    } finally {
      applying.current = false;
    }
  }, []);

  const readLocation = useCallback(() => {
    const next = readEntityRoute(window.location.href);
    intent.current = next;
    setRoute(next);
    setResolution(null);
    // Clear only as part of an explicit browser navigation, never a resize.
    applySelection(null);
    if (next.target?.kind === "doc") viewCallback.current("docs");
    else if (next.target?.kind === "note") viewCallback.current("notes");
    else if (next.target?.kind === "issue" && !window.location.hash) viewCallback.current("tasks");
  }, [applySelection]);

  const navigate = useCallback((target: EntityTarget | null, view?: string) => {
    const next = entityRouteUrl(window.location.href, target, view);
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (next !== current) window.history.pushState(null, "", next);
    intent.current = { target, invalid: false };
    setRoute(intent.current);
    setResolution(null);
    applySelection(null);
    if (target?.kind === "doc") viewCallback.current("docs");
    else if (target?.kind === "note") viewCallback.current("notes");
    else if (view) viewCallback.current(view);
  }, [applySelection]);

  useEffect(() => {
    readLocation();
    window.addEventListener("popstate", readLocation);
    window.addEventListener("hashchange", readLocation);
    // Existing tree, markdown, command-menu and task controls select through
    // the store. Only actual UI selection changes become new history entries.
    const unsubscribe = useStore.subscribe((state, previous) => {
      if (applying.current || !intent.current || !state.hydrated || !previous.hydrated) return;
      if (state.activeWorkspaceId !== previous.activeWorkspaceId || state.currentUserId !== previous.currentUserId) return;
      const workspace = state.workspaces.find((w) => w.id === state.activeWorkspaceId);
      if (!workspace || (initialSlug && workspace.slug !== initialSlug)) return;
      if (state.selectedIssueId !== previous.selectedIssueId) {
        const id = state.selectedIssueId;
        if (id) {
          navigate({ kind: "issue", id });
        } else if (!id && intent.current.target?.kind === "issue") navigate(null);
      } else if (state.selectedDocId !== previous.selectedDocId) {
        const id = state.selectedDocId;
        if (id) {
          navigate({ kind: "doc", id });
        } else if (!id && intent.current.target?.kind === "doc") navigate(null, "docs");
      } else if (state.selectedNoteId !== previous.selectedNoteId) {
        const id = state.selectedNoteId;
        if (id) navigate({ kind: "note", id });
        else if (intent.current.target?.kind === "note") navigate(null, "notes");
      }
    });
    return () => {
      unsubscribe();
      window.removeEventListener("popstate", readLocation);
      window.removeEventListener("hashchange", readLocation);
    };
  }, [initialSlug, navigate, readLocation]);

  const workspace = initialSlug
    ? workspaces.find((w) => w.slug === initialSlug)
    : workspaces.find((w) => w.id === activeWorkspaceId);
  const target = route?.target;
  const key = `${currentUserId}:${workspace?.id ?? initialSlug ?? ""}:${target?.kind ?? ""}:${target?.id ?? ""}`;
  const found = !!target && (target.kind === "doc" ? docs : target.kind === "note" ? notes : issues)
    .some((entity) => entity.id === target.id && entity.workspaceId === workspace?.id);
  const workspaceReady = hydrated && !!currentUserId && !!workspace && workspace.id === activeWorkspaceId;

  useEffect(() => {
    if (!target || !workspaceReady || !workspace || !currentUserId) return;
    if (found) {
      applySelection(target);
      setResolution({ key, status: "ready" });
      return;
    }
    const controller = new AbortController();
    const expected = intent.current;
    setResolution({ key, status: "loading" });
    void useStore.getState().ensureWorkspaceEntityTarget({
      workspaceId: workspace.id, kind: target.kind, id: target.id, signal: controller.signal,
    }).then((result) => {
      if (controller.signal.aborted || intent.current !== expected) return;
      if (result.status === "found") {
        applySelection(target);
        setResolution({ key, status: "ready" });
      } else setResolution({ key, status: result.status });
    });
    return () => controller.abort();
  }, [applySelection, attempt, currentUserId, found, key, route, target, workspace, workspaceReady]);

  let status: Resolution = "ready";
  if (!route || !hydrated) status = "loading";
  else if (error) status = "error";
  else if (!currentUserId || route.invalid || (initialSlug && !workspace)) status = "unavailable";
  else if (!workspaceReady) status = "loading";
  else if (target) status = resolution?.key === key && (resolution.status !== "ready" || found) ? resolution.status : "loading";

  const navigateView = useCallback((view: string) => navigate(null, view), [navigate]);
  const openDoc = useCallback((id: string | null) => navigate(id ? { kind: "doc", id } : null, "docs"), [navigate]);
  const openNote = useCallback((id: string | null) => navigate(id ? { kind: "note", id } : null, "notes"), [navigate]);

  return {
    locationReady: route !== null,
    target,
    status,
    hasIntent: !!target || !!route?.invalid,
    workspaceReady,
    navigateView,
    openDoc,
    openNote,
    retry: () => {
      if (error) void useStore.getState().init();
      setAttempt((value) => value + 1);
    },
  };
}
